import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API_BASE_URL from "../services/apiClient";
import BackHeader from "../components/hub/BackHeader";

const StaysPage = ({ onTabChange }) => {
  const { travelKeyToken } = useParams();
  const navigate = useNavigate();

  const [stays, setStays] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const emptyStay = {
    hotelName: "",
    checkIn: "",
    checkOut: "",
    address: "",
    notes: "",
    fileUrl: "",
  };

  const [newStay, setNewStay] = useState(emptyStay);

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchStays = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/stays`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          setStays(data);
        }
      } catch (err) {
        console.error("Fetch stays error:", err);
      } finally {
        setInitialLoaded(true);
        setLoading(false);
      }
    };

    fetchStays();
  }, [travelKeyToken, navigate]);

  /* ================= AUTO SAVE ================= */
  useEffect(() => {
    if (!initialLoaded) return;

    const saveStays = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      try {
        await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/stays`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ stays }),
        });
      } catch (err) {
        console.error("Save stays error:", err);
      }
    };

    const timer = setTimeout(saveStays, 1000);
    return () => clearTimeout(timer);
  }, [stays, initialLoaded, travelKeyToken]);

  /* ================= ACTIONS ================= */
  const handleAddStay = () => {
    if (!newStay.hotelName) return;
    setStays([...stays, { ...newStay, id: Date.now() }]);
    setNewStay(emptyStay);
    setShowAddForm(false);
  };

  const removeStay = (id) => {
    setStays(stays.filter((s) => s.id !== id));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20">
        <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-xs font-black uppercase tracking-widest text-gray-400">Syncing Stays...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {onTabChange && (
        <BackHeader title="Accommodation" onBack={() => onTabChange("menu")} />
      )}
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-gray-900 mb-2">Accommodation</h1>
          <p className="text-gray-500 font-medium">Keep track of your hotel bookings and stay details.</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-black text-white px-8 py-4 rounded-full font-bold shadow-2xl shadow-gray-200 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 w-fit"
        >
          <span className="text-xl">+</span> Add Stay
        </button>
      </div>

      {/* Add Stay Form Card */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] mb-12"
          >
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-black">Booking Details</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-50 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Hotel or Stay Name</label>
                <input
                  placeholder="e.g. Taj Palace, Airbnb in Tokyo"
                  value={newStay.hotelName}
                  onChange={(e) => setNewStay({ ...newStay, hotelName: e.target.value })}
                  className="w-full bg-gray-50 border-none rounded-[20px] px-6 py-5 focus:ring-2 focus:ring-black transition-all font-bold placeholder:text-gray-300"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Check-in Date</label>
                <input
                  type="date"
                  value={newStay.checkIn}
                  onChange={(e) => setNewStay({ ...newStay, checkIn: e.target.value })}
                  className="w-full bg-gray-50 border-none rounded-[20px] px-6 py-5 focus:ring-2 focus:ring-black transition-all font-bold"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Check-out Date</label>
                <input
                  type="date"
                  value={newStay.checkOut}
                  onChange={(e) => setNewStay({ ...newStay, checkOut: e.target.value })}
                  className="w-full bg-gray-50 border-none rounded-[20px] px-6 py-5 focus:ring-2 focus:ring-black transition-all font-bold"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Address</label>
                <input
                  placeholder="Full address or location name"
                  value={newStay.address}
                  onChange={(e) => setNewStay({ ...newStay, address: e.target.value })}
                  className="w-full bg-gray-50 border-none rounded-[20px] px-6 py-5 focus:ring-2 focus:ring-black transition-all font-bold placeholder:text-gray-300"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Important Notes</label>
                <textarea
                  placeholder="Check-in codes, contact numbers, or special requests..."
                  value={newStay.notes}
                  onChange={(e) => setNewStay({ ...newStay, notes: e.target.value })}
                  className="w-full bg-gray-50 border-none rounded-[20px] px-6 py-5 focus:ring-2 focus:ring-black transition-all font-bold placeholder:text-gray-300 min-h-[120px]"
                />
              </div>

              <div className="md:col-span-2 space-y-2">
                <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">Booking Confirmation (URL)</label>
                {/* 🔥 CLOUDINARY UPLOAD */}
<div className="md:col-span-2 space-y-2">
  <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">
    Or Upload Confirmation (PDF / Image)
  </label>

  <input
    type="file"
    accept="image/*,.pdf"
    onChange={async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        alert("Max 5MB file allowed");
        return;
      }

      setUploading(true);

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "expeditio_upload");

        const res = await fetch(
          "https://api.cloudinary.com/v1_1/dm5ifgtgw/auto/upload",
          {
            method: "POST",
            body: formData,
          }
        );

        const data = await res.json();

        setNewStay((prev) => ({
          ...prev,
          fileUrl: data.secure_url,
        }));

      } catch (err) {
        console.error("Upload error:", err);
      } finally {
        setUploading(false);
      }
    }}
    className="w-full bg-gray-50 border-none rounded-[20px] px-6 py-4 font-bold"
  />

  {/* STATUS */}
  {uploading && (
    <p className="text-xs text-gray-500 font-bold">
      ⏳ Uploading...
    </p>
  )}

  {!uploading && newStay.fileUrl && (
    <p className="text-xs text-green-600 font-bold">
      ✅ File ready
    </p>
  )}

  {/* 🔥 PREVIEW */}
  {newStay.fileUrl && (
    <a
      href={newStay.fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs text-indigo-600 font-bold underline"
    >
      🔍 Preview file
    </a>
  )}
</div>
              </div>
            </div>

            <div className="mt-10 flex flex-col md:flex-row justify-end gap-4 border-t border-gray-50 pt-8">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-8 py-5 rounded-full font-bold text-gray-500 hover:bg-gray-50 transition-all text-center"
              >
                Discard
              </button>
              <button
                onClick={handleAddStay}
                className="bg-black text-white px-12 py-5 rounded-full font-bold shadow-xl active:scale-95 transition-all text-center"
              >
                Save Stay
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stays List */}
      <div className="space-y-8">
        {stays.length === 0 ? (
          <div className="bg-white rounded-[40px] p-24 border-2 border-dashed border-gray-100 flex flex-col items-center text-center">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center text-5xl mb-8 group-hover:scale-110 transition-transform duration-500">🏨</div>
            <h3 className="text-3xl font-black text-gray-900 mb-3 tracking-tight">No accommodations listed</h3>
            <p className="text-gray-400 font-medium mb-10 max-w-sm leading-relaxed">
              Your home away from home is missing. Add your first hotel or Airbnb booking to see it here.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-gray-950 text-white px-10 py-4 rounded-full font-bold shadow-2xl hover:bg-black transition-all"
            >
              + Record a Stay
            </button>
          </div>
        ) : (
          stays.map((stay, index) => (
            <motion.div
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              key={stay.id || index}
              className="group bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm hover:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.05)] transition-all relative overflow-hidden"
            >
              {/* Decorative accent */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50/50 rounded-bl-[100px] -z-0 translate-x-12 -translate-y-12 transition-transform group-hover:scale-125"></div>

              <div className="relative z-10">
                <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-10">
                  <div className="flex gap-8">
                    <div className="w-20 h-20 bg-gray-900 rounded-[28px] flex items-center justify-center text-4xl shrink-0 shadow-xl shadow-gray-200 group-hover:-rotate-3 transition-all duration-500">
                      🏨
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full">Confirmed</span>
                      </div>
                      <h3 className="text-3xl font-black text-gray-900 mb-2 leading-none tracking-tight">{stay.hotelName}</h3>
                      <div className="flex items-center gap-2 text-gray-400 group-hover:text-gray-600 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm font-bold">{stay.address || "Location not set"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 md:gap-8 bg-gray-50 rounded-3xl p-6 px-8 border border-gray-100/50">
                    <div className="text-center">
                      <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-1">Check-in</p>
                      <p className="text-lg font-black text-gray-900">
                        {stay.checkIn ? new Date(stay.checkIn).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '---'}
                      </p>
                    </div>
                    <div className="w-px h-10 bg-gray-200 self-center"></div>
                    <div className="text-center">
                      <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-1">Check-out</p>
                      <p className="text-lg font-black text-gray-900">
                        {stay.checkOut ? new Date(stay.checkOut).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '---'}
                      </p>
                    </div>
                  </div>
                </div>

                {stay.notes && (
                  <div className="bg-gray-50/50 rounded-3xl p-6 mb-8 border border-gray-100/30">
                    <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest mb-2">Guest Notes</p>
                    <p className="text-sm text-gray-600 font-medium leading-relaxed">{stay.notes}</p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    {stay.fileUrl && (
                      <a
                        href={stay.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-xs font-black uppercase text-black hover:text-indigo-600 transition-all bg-gray-50 hover:bg-indigo-50 px-5 py-3 rounded-2xl"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View Confirmation
                      </a>
                    )}
                  </div>
                  <button
                    onClick={() => removeStay(stay.id)}
                    className="p-4 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all active:scale-90"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default StaysPage;